import { windowsInstallCommand, windowsUpgradeCommand, macosUpgradeCommand, type WindowsShell } from './windows-install';
import {ENDPOINTS} from './endpoints';
import { displayLanguage } from '../displayLanguage';
import { LanguageSwitch } from '../LanguageSwitch';
import {
  BankOutlined, CheckCircleFilled, ClockCircleOutlined, CloudOutlined, CopyOutlined, DeleteOutlined,
  CloudServerOutlined, DownOutlined, EllipsisOutlined, FileSearchOutlined, LaptopOutlined, LogoutOutlined, MenuFoldOutlined,
  MenuUnfoldOutlined, MoonOutlined, PlusOutlined, ReloadOutlined,
  SafetyCertificateOutlined, SunOutlined, UserOutlined,
} from '@ant-design/icons';
import {
  Alert, Badge, Breadcrumb, Button, Card, Descriptions, Dropdown, Empty, Form,
  Input, Layout, Modal, Popconfirm, Result, Segmented, Skeleton, Space, Tag, Typography,
} from 'antd';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trans } from '../i18n/Trans';
import { i18n } from '../i18n';
import { useFormat, type Formatters } from '../i18n/format';
import type { Mode } from '../Root';
import { ApiError, controlApi } from './api';
import type { Account, CloudOffer, CreatedZone, CreateZoneRequest, FirmPlatform, InstallCommand, InstallStep, Zone, ZoneTopology } from './api';
import { cloudOf, cloudRefreshMs, cloudStage, CLOUD_TRANSITION_REFRESH_MS, defaultRegion, recreateAction, RECREATE_RETRY_MS, wakeDidNotFinish } from './cloud';
import { CloudChip, cloudCardNote, CloudDetailsFields, CloudStart, OpenZoneButton, PowerPanel, regionLabel, storageLine, WakeBeforeWork } from './CloudZone';
import { holdComposingEnter } from './composition';
import { exampleHandle, HANDLE, HANDLE_MAX, suggestHandle, type HandleCheck } from './handle';
import '../styles/control.css';
import { SupportReports } from './SupportReports';
import { RecoveryPage } from './Recovery';
import { refreshFailureAction, refreshOnReturn, zonesNeedRefresh, ZONE_REFRESH_MS } from './zone-refresh';

const { Header, Sider, Content } = Layout;
const { Paragraph, Text, Title } = Typography;
const CLAIM_TTL_MS = 24 * 60 * 60 * 1_000;
// W1a ships the complete UI dormant. W5 enables it only after a validated
// Windows artifact and stamped installer are published and probed.
const WINDOWS_INSTALL_ENABLED = import.meta.env.VITE_WINDOWS_INSTALL_ENABLED === 'true';
// The firm release gate's frontend twin (multi-user spec §3.10): off in production until the Word
// and Outlook add-ins ship, on in staging. The Worker enforces it too.
const FIRM_DEPLOYMENT_ENABLED = import.meta.env.VITE_FIRM_DEPLOYMENT_ENABLED === 'true';
/** Zones per account and topology (spec §3.10); the Worker enforces them. */
const ZONE_QUOTAS = { laptop: 2, firm: 1, cloud: 1 } as const;
// The stale threshold remains 48h even though config signaling now checks in
// hourly; connectivity display policy is independent of rotation latency.
const STALE_AFTER_MS = 48 * 60 * 60 * 1_000;

type ConsoleT = TFunction<'console'>;
// eslint-disable-next-line i18next/no-literal-string -- the product name, the same in every language
const BRAND = 'LexSide';
/** The console's messages in the current language, for callbacks that outlive a render. */
const consoleT = (): ConsoleT => i18n.getFixedT(null, 'console');

interface Navigate { (path: string, replace?: boolean): void }
interface CommonProps {
  readonly account: Account;
  readonly mode: Mode;
  readonly navigate: Navigate;
  readonly onToggleTheme: () => void;
  readonly onLogout: () => Promise<void>;
  readonly onUnauthorized: () => void;
}

function errorMessage(t: ConsoleT, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 404) return t('errors.zoneNotFound');
    if (error.code === 'zone_not_awaiting_setup') return t('errors.notAwaitingSetup');
  }
  return t('common.unreachable');
}

function dateTime(t: ConsoleT, format: Formatters, value: number | null): string {
  if (value === null) return t('common.notYet');
  return format.date(new Date(value), { dateStyle: 'medium', timeStyle: 'short' });
}

function relativeTime(format: Formatters, value: number): string {
  const seconds = Math.round((value - Date.now()) / 1_000);
  const abs = Math.abs(seconds);
  const [amount, unit]: readonly [number, Intl.RelativeTimeFormatUnit] = abs < 60
    ? [seconds, 'second'] as const
    : abs < 3_600
      ? [Math.round(seconds / 60), 'minute'] as const
      : abs < 86_400
        ? [Math.round(seconds / 3_600), 'hour'] as const
        : [Math.round(seconds / 86_400), 'day'] as const;
  return format.relative(amount, unit, { numeric: 'auto' });
}

function isOffline(zone: Zone): boolean {
  // A cloud zone that sleeps checks in only while awake: its chip is power (§3.2.3).
  return topologyOf(zone) !== 'cloud' && zone.status === 'connected' && zone.lastCheckinAt !== null && Date.now() - zone.lastCheckinAt > STALE_AFTER_MS;
}

function ZoneStatusTag({ zone }: { readonly zone: Zone }) {
  const { t } = useTranslation('console');
  if (topologyOf(zone) === 'cloud') return <CloudChip zone={zone} />;
  if (isOffline(zone)) return <Tag><Badge status="default" /> {t('zone.status.offline')}</Tag>;
  if (zone.status === 'connected') return <Tag color="success"><Badge status="success" /> {t('zone.status.connected')}</Tag>;
  if (zone.status === 'claimed') return <Tag color="processing"><Badge status="processing" /> {t('zone.status.claimed')}</Tag>;
  return <Tag color="warning"><Badge status="warning" /> {t('zone.status.awaitingSetup')}</Tag>;
}

function ClaimExpiry({ zone }: { readonly zone: Zone }) {
  const { t } = useTranslation('console');
  const format = useFormat();
  if (zone.status !== 'awaiting_setup' || zone.claimExpiresAt === null || topologyOf(zone) === 'cloud') return null;
  const expired = zone.claimExpiresAt <= Date.now();
  return <Text type={expired ? 'danger' : 'secondary'} className="cp-expiry">
    {expired ? t('zone.claim.expired') : t('zone.claim.expires', { when: relativeTime(format, zone.claimExpiresAt) })}
  </Text>;
}

function topologyOf(zone: Zone): ZoneTopology {
  return zone.topology ?? 'laptop';
}

/** A firm zone's address, which its lawyers' browsers open (spec §3.9). */
function zoneAddress(zone: Zone): string | null {
  // eslint-disable-next-line i18next/no-literal-string -- an address
  return topologyOf(zone) === 'firm' && zone.zoneHost ? `https://${zone.zoneHost}:47800` : null;
}

function TopologyIcon({ zone }: { readonly zone: Zone }) {
  const topology = topologyOf(zone);
  return topology === 'cloud' ? <CloudOutlined /> : topology === 'firm' ? <BankOutlined /> : <LaptopOutlined />;
}

/** "Cloud · ‹region name›" wherever a topology appears (§3.2.3). */
function topologyName(t: ConsoleT, zone: Zone): string {
  const topology = topologyOf(zone);
  const cloud = cloudOf(zone);
  return topology === 'cloud' ? cloud === null ? t('zone.topology.cloud') : t('zone.topology.cloudRegion', { region: regionLabel(t, cloud.region) })
    : topology === 'firm' ? t('zone.topology.firm') : t('zone.topology.laptop');
}

function TopologyTag({ zone }: { readonly zone: Zone }) {
  const { t } = useTranslation('console');
  return <Tag icon={<TopologyIcon zone={zone} />}>{topologyName(t, zone)}</Tag>;
}

/** A zone card's handle line (card 23); nothing from a Worker that predates handles. */
function ZoneHandleLine({ zone }: { readonly zone: Zone }) {
  const { t } = useTranslation('console');
  if (zone.handle === undefined) return null;
  return <span className="cp-zone-handle">{t('zone.handle.label')} {zone.handle === null
    ? <span className="cp-zone-handle-unset">{t('zone.handle.notSet')}</span> : <code>{zone.handle}</code>}</span>;
}

/** Zone information's Handle row (card 24). Set once, in the zone's own web app (readable intake addresses §3.1.4, §3.7). */
function handleFact(t: ConsoleT, zone: Zone, span: number | 'filled' = 1): React.ReactNode {
  if (zone.handle === undefined) return null;
  return <Descriptions.Item label={t('zone.handle.label')} span={span}>{zone.handle === null
    ? <Text type="secondary">{t('zone.handle.notSet')}</Text> : <span className="cp-handle-value">{zone.handle}</span>}</Descriptions.Item>;
}

/**
 * Server-provided install copy: a backtick pair marks code, and a backslash makes the next
 * character literal (the server escapes the values it interpolates). An unmatched backtick is
 * literal text.
 */
export function installCopySegments(text: string): { readonly code: boolean; readonly text: string }[] {
  const segments: { code: boolean; text: string }[] = [];
  let current = '';
  let code = false;
  let codeStart = -1;
  const flush = () => { if (current !== '') segments.push({ code, text: current }); current = ''; };
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]!;
    if (character === '\\' && index + 1 < text.length) { current += text[index + 1]; index += 1; }
    else if (character === '`') {
      if (!code) { flush(); code = true; codeStart = segments.length; }
      else { flush(); code = false; }
    } else current += character;
  }
  if (code) {
    // No closing backtick: what followed the opening one is ordinary text after a literal "`".
    flush();
    const opened = segments.splice(codeStart).map(segment => segment.text).join('');
    segments.push({ code: false, text: `\`${opened}` });
  } else flush();
  return segments;
}

function InstallCopy({ text, mono = false }: { readonly text: string; readonly mono?: boolean }) {
  return <>{installCopySegments(text).map((segment, index) => !segment.code ? segment.text
    : mono ? <span key={index} style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{segment.text}</span> : <code key={index}>{segment.text}</code>)}</>;
}

function ThemeButton({ mode, onToggle }: { readonly mode: Mode; readonly onToggle: () => void }) {
  const { t } = useTranslation('console');
  return <Button type="text" className="cp-theme-button"
    icon={mode === 'light' ? <MoonOutlined /> : <SunOutlined />}
    aria-label={mode === 'light' ? t('shell.themeDark') : t('shell.themeLight')} onClick={onToggle} />;
}

export function AuthScreen({ mode, onToggleTheme, onAuthenticated }: {
  readonly mode: Mode;
  readonly onToggleTheme: () => void;
  readonly onAuthenticated: (account: Account) => void;
}) {
  const { t } = useTranslation('console');
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const requestCode = async (values: { email: string }) => {
    setSubmitting(true); setError(null);
    try {
      await controlApi.issueCode(values.email, displayLanguage.language() ?? undefined);
      setEmail(values.email.trim()); setStep('code'); setNotice(null);
    } catch { setError(t('auth.requestFailed')); }
    finally { setSubmitting(false); }
  };

  const verifyCode = async (values: { code: string }) => {
    setSubmitting(true); setError(null);
    try {
      const result = await controlApi.verifyCode(email, values.code);
      onAuthenticated(result.account);
    } catch (caught) {
      setError(caught instanceof ApiError && caught.code === 'invalid_code'
        ? t('auth.invalidCode')
        : t('auth.verifyFailed'));
    } finally { setSubmitting(false); }
  };

  const resend = async () => {
    setSubmitting(true); setError(null);
    try {
      await controlApi.issueCode(email, displayLanguage.language() ?? undefined);
      setNotice(t('auth.resent'));
    } catch { setError(t('auth.resendFailed')); }
    finally { setSubmitting(false); }
  };

  return <main className="cp-auth">
    <div className="cp-auth-theme"><LanguageSwitch className="cp-theme-button" /><ThemeButton mode={mode} onToggle={onToggleTheme} /></div>
    <a className="cp-auth-brand" href="/" aria-label={t('shell.home')}><span className="cp-mark">L</span><span>{BRAND}</span></a>
    <Card className="cp-auth-card">
      {step === 'email' ? <>
        <Title level={2}>{t('auth.title')}</Title>
        <Paragraph type="secondary">{t('auth.intro')}</Paragraph>
        {error !== null && <Alert type="error" showIcon message={error} />}
        <Form layout="vertical" requiredMark={false} onFinish={requestCode}>
          <Form.Item name="email" label={t('auth.emailLabel')} rules={[
            { required: true, message: t('auth.emailRequired') },
            { type: 'email', message: t('auth.emailInvalid') },
          ]}><Input autoComplete="email" autoFocus placeholder="you@firm.com" size="large" onKeyDown={holdComposingEnter} /></Form.Item>
          <Button type="primary" htmlType="submit" block size="large" loading={submitting}>{t('auth.submit')}</Button>
        </Form>
        <Paragraph className="cp-auth-privacy" type="secondary">
          {t('auth.privacy')}
        </Paragraph>
      </> : <>
        <div className="cp-auth-icon"><ClockCircleOutlined /></div>
        <Title level={2}>{t('auth.checkTitle')}</Title>
        <Paragraph type="secondary"><Trans t={t} i18nKey="auth.checkBody" values={{ email }} components={{ email: <Text strong /> }} /></Paragraph>
        {notice !== null && <Alert type="success" showIcon message={notice} />}
        {error !== null && <Alert type="error" showIcon message={error} />}
        <Form layout="vertical" requiredMark={false} onFinish={verifyCode}>
          <Form.Item name="code" label={t('auth.codeLabel')}
            normalize={(value: string) => value.replace(/\D/gu, '').slice(0, 6)}
            rules={[{ required: true, pattern: /^\d{6}$/u, message: t('auth.codeRequired') }]}>
            <Input className="cp-code-input" autoComplete="one-time-code" autoFocus inputMode="numeric" maxLength={6} placeholder="000000" size="large" onKeyDown={holdComposingEnter} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block size="large" loading={submitting}>{t('auth.continue')}</Button>
        </Form>
        <div className="cp-auth-links">
          <Button type="link" onClick={() => void resend()} loading={submitting}>{t('auth.resend')}</Button>
          <Button type="link" onClick={() => { setStep('email'); setError(null); setNotice(null); }}>{t('auth.otherEmail')}</Button>
        </div>
      </>}
    </Card>
    <Text type="secondary" className="cp-auth-foot">{t('auth.foot')}</Text>
  </main>;
}

function ControlShell({ account, mode, navigate, onToggleTheme, onLogout, zones,
  zonesLoading, activeZoneId, path, children }:
  CommonProps & { readonly zones: readonly Zone[]; readonly zonesLoading: boolean;
    readonly activeZoneId: string | null; readonly path: string; readonly children: React.ReactNode }) {
  const { t } = useTranslation('console');
  const [collapsed, setCollapsed] = useState(false);
  const [zonesOpen, setZonesOpen] = useState(true);
  return <Layout className="cp-shell">
    <Header className="cp-header">
      <button className="cp-brand" onClick={() => navigate('/zones')}><span className="cp-mark">L</span><span>{BRAND}</span></button>
      <span className="cp-header-divider" /><Text type="secondary">{t('shell.controlPlane')}</Text>
      <Space className="cp-header-actions" size={4}>
        <LanguageSwitch className="cp-theme-button" />
        <ThemeButton mode={mode} onToggle={onToggleTheme} />
        <Dropdown trigger={['click']} menu={{
          items: [{ key: 'logout', icon: <LogoutOutlined />, label: t('shell.logOut') }],
          onClick: () => void onLogout(),
        }}><Button type="text" className="cp-account-button" icon={<UserOutlined />}>{account.email}</Button></Dropdown>
      </Space>
    </Header>
    <Layout>
      <Sider className="cp-sider" width={238} collapsedWidth={63} collapsed={collapsed}>
        <div className="cp-nav-group">
          <div className="cp-nav-group-row">
            <button className={`cp-nav-group-head${activeZoneId === null && !path.startsWith('/control/') ? ' is-active' : ''}`}
              onClick={() => navigate('/zones')} title={t('shell.zones')}>
              <span className="cp-nav-icon"><SafetyCertificateOutlined /></span>
              {!collapsed && <span className="cp-nav-label">{t('shell.zones')}</span>}
            </button>
            {!collapsed && <button className="cp-nav-toggle"
              onClick={() => setZonesOpen((open) => !open)}
              aria-label={zonesOpen ? t('shell.collapseZones') : t('shell.expandZones')}
              aria-expanded={zonesOpen} aria-controls="cp-zone-nav-children">
              <span className={`cp-nav-caret${zonesOpen ? '' : ' is-closed'}`}><DownOutlined /></span>
            </button>}
          </div>
          {zonesOpen && !collapsed && <div className="cp-nav-children" id="cp-zone-nav-children">
            {zonesLoading ? <p className="cp-nav-empty">{t('shell.loadingZones')}</p>
              : zones.length === 0 ? <p className="cp-nav-empty">{t('shell.noZones')}</p>
                : zones.map((zone) => <button key={zone.id}
                  className={`cp-nav-child${zone.id === activeZoneId ? ' is-active' : ''}`}
                  onClick={() => navigate(`/zones/${zone.id}`)} title={zone.name}>
                  <span className="cp-nav-dot" aria-hidden="true" />
                  <span className="cp-nav-name">{zone.name}</span>
                </button>)}
          </div>}
        </div>
        <div className="cp-nav-group"><button
          className={`cp-nav-group-head${path === '/control/recovery' ? ' is-active' : ''}`}
          onClick={() => navigate('/control/recovery')} title={t('shell.recovery')}>
          <span className="cp-nav-icon"><CloudServerOutlined /></span>
          {!collapsed && <span className="cp-nav-label">{t('shell.recovery')}</span>}
        </button></div>
        <div className="cp-nav-group"><button
          className={`cp-nav-group-head${path.startsWith('/control/support/') ? ' is-active' : ''}`}
          onClick={() => navigate('/control/support/reports')} title={t('shell.support')}>
          <span className="cp-nav-icon"><FileSearchOutlined /></span>
          {!collapsed && <span className="cp-nav-label">{t('shell.support')}</span>}
        </button></div>
        <button className="cp-collapse" onClick={() => setCollapsed((value) => !value)} aria-label={collapsed ? t('shell.expandNavigation') : t('shell.collapseNavigation')}>
          <span className="cp-nav-icon">{collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}</span>
          {!collapsed && <span className="cp-nav-label">{t('shell.collapse')}</span>}
        </button>
      </Sider>
      <Content className="cp-content">{children}</Content>
    </Layout>
  </Layout>;
}

function defaultInstallPlatform(): FirmPlatform {
  return WINDOWS_INSTALL_ENABLED && typeof navigator !== 'undefined' &&
    /Win/u.test(navigator.platform) ? 'windows' : 'macos';
}


// The short command is typed into PowerShell; Command Prompt gets its own (short Windows install command spec §3.3).
const WINDOWS_SHELLS: readonly WindowsShell[] = ['powershell', 'cmd'];

function ShellChoice({ value, onChange }: { readonly value: WindowsShell; readonly onChange: (shell: WindowsShell) => void }) {
  const { t } = useTranslation('console');
  return <Segmented<WindowsShell> className="cp-install-shell" size="small" options={WINDOWS_SHELLS.map(shell => ({ label: t(`install.shell.${shell}`), value: shell }))} value={value} onChange={onChange} />;
}

function CopyCommand({ text, style }: { readonly text: string; readonly style?: React.CSSProperties }) {
  const { t } = useTranslation('console');
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [text]);
  return <div className="cp-command" style={style}><code>{text}</code>
    <Button icon={copied ? <CheckCircleFilled /> : <CopyOutlined />} onClick={() => {
      void navigator.clipboard.writeText(text).then(() => setCopied(true));
    }}>{copied ? t('install.copied') : t('install.copy')}</Button></div>;
}

const FIRM_PLATFORMS = [
  // eslint-disable-next-line i18next/no-literal-string -- operating system names, the same in every language
  { label: 'macOS', value: 'macos' }, { label: 'Windows', value: 'windows' }, { label: 'Linux', value: 'linux' },
] as const;
const LAPTOP_PLATFORMS = FIRM_PLATFORMS.filter(platform => platform.value !== 'linux');

function UpgradeCommandCard({ zone }: { readonly zone: Zone }) {
  const { t } = useTranslation('console');
  const firm = topologyOf(zone) === 'firm';
  const [platform, setPlatform] = useState<FirmPlatform>(defaultInstallPlatform);
  const [shell, setShell] = useState<WindowsShell>('powershell');
  // macOS and Linux share install.sh, which branches on uname.
  const text = platform === 'windows' ? windowsUpgradeCommand(ENDPOINTS, shell) : macosUpgradeCommand(ENDPOINTS);
  const shellChoice = platform === 'windows' && <>
    <ShellChoice value={shell} onChange={setShell} />
    <Paragraph className="cp-command-label">{t('install.runIn', { shell: t(`install.shell.${shell}`) })}</Paragraph>
  </>;
  if (!firm) {
    return <Card title={t('upgrade.title')} className="cp-facts">
      <Paragraph type="secondary">{t('upgrade.laptop')}</Paragraph>
      {WINDOWS_INSTALL_ENABLED && <Segmented<FirmPlatform> className="cp-install-platform"
        options={[...LAPTOP_PLATFORMS]}
        value={platform === 'linux' ? 'macos' : platform} onChange={setPlatform} />}
      {shellChoice}
      <CopyCommand text={text} />
    </Card>;
  }
  return <Card title={t('upgrade.title')} className="cp-facts">
    <Paragraph type="secondary">{t('upgrade.firm')}</Paragraph>
    <Alert type="info" showIcon message={t('upgrade.rollbackTitle')}
      description={t('upgrade.rollback')} />
    <Segmented<FirmPlatform> className="cp-install-platform" style={{ marginTop: 14 }} options={[...FIRM_PLATFORMS]} value={platform} onChange={setPlatform} />
    {shellChoice}
    <CopyCommand text={text} style={{ marginTop: 12 }} />
  </Card>;
}

function FirmInstallStep({ step, command }: { readonly step: InstallStep; readonly command: string }) {
  return <li><div>
    <b><InstallCopy text={step.title} mono /></b>{step.text !== undefined && <> <InstallCopy text={step.text} /></>}
    {step.command === true && <CopyCommand text={command} />}
    {step.body !== undefined && <small><InstallCopy text={step.body} /></small>}
    {step.note !== undefined && <small><InstallCopy text={step.note} /></small>}
  </div></li>;
}

function InstallCommandModal({ open, zone, command, onClose }: {
  readonly open: boolean; readonly zone: Zone | null; readonly command: InstallCommand | null; readonly onClose: () => void;
}) {
  const { t } = useTranslation('console');
  const format = useFormat();
  const [copied, setCopied] = useState(false);
  const [platform, setPlatform] = useState<FirmPlatform>(defaultInstallPlatform);
  const [shell, setShell] = useState<WindowsShell>('powershell');
  useEffect(() => {
    setCopied(false);
    setPlatform(defaultInstallPlatform());
    setShell('powershell');
  }, [command, open]);
  if (command === null || zone === null) return null;
  const expires = <Paragraph type="secondary" className="cp-command-expiry"><ClockCircleOutlined /> {t('install.expires', { when: dateTime(t, format, command.claimExpiresAt ?? command.zone?.claimExpiresAt ?? Date.now() + CLAIM_TTL_MS) })}</Paragraph>;
  const instructions = command.instructions;
  if (instructions !== undefined) {
    // A firm zone (spec §3.10, reference design P5): numbered steps for each server platform.
    const firmCommand = platform === 'windows'
      ? windowsInstallCommand(command.claimToken, ENDPOINTS, zone.zoneHost ?? command.zone?.zoneHost, shell)
      : command.installCommand;
    return <Modal title={t('install.title', { name: zone.name })} open={open} footer={null} onCancel={onClose} width={760} style={{ top: 40 }} destroyOnClose>
      <Alert type="warning" showIcon message={t('install.saveNow')}
        description={t('install.saveNowFirm')} />
      <Segmented<FirmPlatform> className="cp-install-platform" block style={{ marginTop: 16 }}
        value={platform} options={[...FIRM_PLATFORMS]} onChange={setPlatform} />
      {platform === 'windows' && <ShellChoice value={shell} onChange={setShell} />}
      <ol className="cp-install-steps">
        {instructions.platforms[platform].map((step, index) => <FirmInstallStep key={index} step={step} command={firmCommand} />)}
      </ol>
      {expires}
      <Paragraph type="secondary" className="cp-command-hint">{instructions.expiry}</Paragraph>
      <div className="cp-modal-footer"><Button type="primary" onClick={onClose}>{t('install.saved')}</Button></div>
    </Modal>;
  }
  const laptopPlatform: FirmPlatform = platform === 'linux' ? 'macos' : platform;
  const commandText = laptopPlatform === 'windows'
    ? windowsInstallCommand(command.claimToken, ENDPOINTS, null, shell)
    : command.installCommand;
  return <Modal title={t('install.title', { name: zone.name })} open={open} footer={null} onCancel={onClose} width={680} destroyOnClose>
    <Alert type="warning" showIcon message={t('install.saveNow')}
      description={t('install.saveNowLaptop')} />
    {WINDOWS_INSTALL_ENABLED && <Segmented<FirmPlatform> className="cp-install-platform"
      block value={laptopPlatform} options={[...LAPTOP_PLATFORMS]} onChange={(value) => { setPlatform(value); setCopied(false); }} />}
    {laptopPlatform === 'windows' && <ShellChoice value={shell} onChange={(value) => { setShell(value); setCopied(false); }} />}
    <Paragraph className="cp-command-label">{laptopPlatform === 'windows'
      ? t('install.runOnWindows', { shell: t(`install.shell.${shell}`) })
      : t('install.runOnMac')}</Paragraph>
    <div className="cp-command"><code>{commandText}</code>
      <Button icon={copied ? <CheckCircleFilled /> : <CopyOutlined />} onClick={() => {
        void navigator.clipboard.writeText(commandText).then(() => setCopied(true));
      }}>{copied ? t('install.copied') : t('install.copy')}</Button>
    </div>
    {laptopPlatform === 'macos' ? <Paragraph type="secondary" className="cp-command-hint">
      <Trans t={t} i18nKey="install.keychain" components={{ strong: <Text strong /> }} />
    </Paragraph> : <Paragraph type="secondary" className="cp-command-hint">
      {t('install.credentialManager')}
    </Paragraph>}
    {expires}
    <div className="cp-modal-footer"><Button type="primary" onClick={onClose}>{t('install.saved')}</Button></div>
  </Modal>;
}

function DeleteZoneModal({ zone, open, onClose, onDeleted, onUnauthorized }: {
  readonly zone: Zone | null; readonly open: boolean; readonly onClose: () => void;
  readonly onDeleted: (zone: Zone) => void; readonly onUnauthorized: () => void;
}) {
  const { t } = useTranslation('console');
  const format = useFormat();
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether the zone has backups (backup spec §4.10, card 46): deleting it deletes them. Unknown
  // (the list could not be read) is told as if it had some, since the deletion cannot be undone.
  const [backups, setBackups] = useState<{ readonly lastSuccessAt: number | null } | 'none' | 'unknown' | 'loading'>('loading');
  useEffect(() => { setConfirmation(''); setError(null); }, [zone, open]);
  useEffect(() => {
    if (!open || zone === null) return;
    let current = true;
    setBackups('loading');
    controlApi.backups().then(
      (answer) => { if (current) setBackups(answer.zones.find((item) => item.zoneId === zone.id) ?? 'none'); },
      (caught: unknown) => {
        if (!current) return;
        if (caught instanceof ApiError && caught.status === 401) onUnauthorized();
        else setBackups('unknown');
      },
    );
    return () => { current = false; };
  }, [zone, open, onUnauthorized]);
  if (zone === null) return null;
  const server = topologyOf(zone) === 'firm';
  const cloud = topologyOf(zone) === 'cloud';
  const withBackups = !cloud && backups !== 'none' && backups !== 'loading';
  const remove = async () => {
    setDeleting(true); setError(null);
    try { await controlApi.deleteZone(zone.id); onDeleted(zone); onClose(); }
    catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onUnauthorized();
      else setError(errorMessage(t, caught));
    } finally { setDeleting(false); }
  };
  return <Modal title={t('delete.title')} open={open} onCancel={onClose} okText={cloud ? t('delete.okCloud') : withBackups ? t('delete.okBackups') : t('delete.ok')}
    okButtonProps={{ danger: true, disabled: confirmation !== zone.name || backups === 'loading', loading: deleting }} onOk={() => void remove()}>
    {withBackups && <>
      <Alert type="error" showIcon message={t('delete.backupsTitle')}
        description={backups === 'unknown' ? t('delete.backupsUnknown')
          : backups.lastSuccessAt === null ? t('delete.backupsNone')
            : t('delete.backupsLast', { when: format.date(new Date(backups.lastSuccessAt), { dateStyle: 'medium', timeStyle: 'short' }) })} />
      <Paragraph className="cp-confirm-copy"><Trans t={t} i18nKey={server ? 'delete.replaceServer' : 'delete.replaceLaptop'} components={{ strong: <Text strong /> }} /></Paragraph>
    </>}
    {cloud
      ? <Alert type="error" showIcon message={t('delete.cloudTitle')}
        description={<ul className="cp-delete-list">
          <li>{t('delete.cloudDocuments')}</li>
          <li>{t('delete.cloudProjects')}</li>
          <li>{t('delete.cloudVault')}</li>
          <li>{t('delete.cloudNoUndo')}</li>
        </ul>} />
      : topologyOf(zone) === 'firm'
      ? <Alert type="warning" showIcon message={t('delete.firmTitle')}
        description={t('delete.firmDetail')} />
      : <Alert type="warning" showIcon message={t('delete.laptopTitle')}
        description={t('delete.laptopDetail')} />}
    <Paragraph className="cp-confirm-copy"><Trans t={t} i18nKey="delete.typeToConfirm" values={{ name: zone.name }} components={{ code: <Text code /> }} /></Paragraph>
    <Input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoFocus />
    {error !== null && <Alert className="cp-inline-error" type="error" showIcon message={error} />}
  </Modal>;
}

function ZoneCard({ zone, navigate, onDelete }: {
  readonly zone: Zone; readonly navigate: Navigate; readonly onDelete: (zone: Zone) => void;
}) {
  const { t } = useTranslation('console');
  const format = useFormat();
  const cloud = cloudOf(zone);
  if (cloud !== null) return <CloudZoneCard zone={zone} webName={cloud.webName} navigate={navigate} onDelete={onDelete} />;
  const note = zone.status === 'awaiting_setup'
    ? zone.claimExpiresAt !== null && zone.claimExpiresAt <= Date.now()
      ? t('zone.card.setupIncomplete')
      : t('zone.card.runInstall')
    : zone.status === 'claimed'
      ? t('zone.card.claimed')
      : isOffline(zone)
        ? zone.lastCheckinAt === null ? t('zone.card.neverSeen') : t('zone.card.lastSeen', { when: relativeTime(format, zone.lastCheckinAt) })
        : zone.lastCheckinAt === null ? t('zone.card.connectedJustNow') : t('zone.card.lastConnected', { when: relativeTime(format, zone.lastCheckinAt) });
  // A firm zone's note leads with its user count from check-in (reference design P1).
  const count = topologyOf(zone) === 'firm' && zone.status === 'connected' && typeof zone.userCount === 'number' ? zone.userCount : null;
  const address = zoneAddress(zone);
  return <Card className="cp-zone-card"
    title={<button className="cp-card-title" onClick={() => navigate(`/zones/${zone.id}`)}>{zone.name}</button>}
    extra={<Dropdown trigger={['click']} menu={{
      items: [{ key: 'delete', danger: true, icon: <DeleteOutlined />, label: t('zone.deleteZone') }],
      onClick: () => onDelete(zone),
    }}><Button type="text" icon={<EllipsisOutlined />} aria-label={t('zone.card.actions', { name: zone.name })} /></Dropdown>}>
    <Space wrap><TopologyTag zone={zone} /><ZoneStatusTag zone={zone} /></Space>
    {address !== null && <span className="cp-zone-address">{address}</span>}
    <ZoneHandleLine zone={zone} />
    <Paragraph type="secondary" className="cp-zone-note">{count === null ? note
      : t('zone.card.usersNote', { count, note })}</Paragraph><ClaimExpiry zone={zone} />
    <div className="cp-zone-card-footer"><Text type="secondary" className="cp-zone-id">{zone.id}</Text>
      <Button type="primary" ghost onClick={() => navigate(`/zones/${zone.id}`)}>{zone.status === 'awaiting_setup' ? t('zone.card.finishSetup') : t('zone.card.manage')}</Button>
    </div>
  </Card>;
}

/** A cloud zone's card (card 23): power, its web address, and Open zone in every power state. */
function CloudZoneCard({ zone, webName, navigate, onDelete }: {
  readonly zone: Zone; readonly webName: string; readonly navigate: Navigate; readonly onDelete: (zone: Zone) => void;
}) {
  const { t } = useTranslation('console');
  const format = useFormat();
  const { note, when } = cloudCardNote(t, format, zone);
  const count = typeof zone.userCount === 'number' ? t('zone.card.users', { count: zone.userCount }) : null;
  // eslint-disable-next-line i18next/no-literal-string -- the separator between the card's facts
  const meta = [count, when, storageLine(t, format, zone)].filter((part): part is string => part !== null).join(' · ');
  return <Card className="cp-zone-card"
    title={<button className="cp-card-title" onClick={() => navigate(`/zones/${zone.id}`)}>{zone.name}</button>}
    extra={<Dropdown trigger={['click']} menu={{
      items: [{ key: 'delete', danger: true, icon: <DeleteOutlined />, label: t('zone.deleteZone') }],
      onClick: () => onDelete(zone),
    }}><Button type="text" icon={<EllipsisOutlined />} aria-label={t('zone.card.actions', { name: zone.name })} /></Dropdown>}>
    <Space wrap><TopologyTag zone={zone} /><ZoneStatusTag zone={zone} /></Space>
    <span className="cp-zone-address">https://{webName}</span>
    <ZoneHandleLine zone={zone} />
    {meta !== '' && <Text type="secondary" className="cp-zone-meta">{meta}</Text>}
    <Paragraph type="secondary" className="cp-zone-note">{note}</Paragraph>
    <div className="cp-zone-card-footer"><Text type="secondary" className="cp-zone-id">{zone.id}</Text>
      <Space><OpenZoneButton zone={zone} /><Button type="primary" ghost onClick={() => navigate(`/zones/${zone.id}`)}>{t('zone.card.manage')}</Button></Space>
    </div>
  </Card>;
}

type DeployTopology = 'laptop' | 'firm' | 'cloud';
/**
 * The zone name starts from the topology (spec §3.10) until the deployer edits it. It is content once saved, so it is
 * in the deployer's display language (i18n spec §7 decision 11).
 */
function defaultName(t: ConsoleT, topology: DeployTopology): string {
  return topology === 'firm' ? t('deploy.name.defaultServer') : '';
}
interface DeployForm { readonly name: string; readonly handle?: string; readonly ownerEmail?: string; readonly zoneHost?: string; readonly tenantName?: string; readonly region?: string }
const HANDLE_CHECK_MS = 300;
// eslint-disable-next-line i18next/no-literal-string -- an example DNS name
const ZONE_HOST_EXAMPLE = 'lexside.firm.internal';
const ZONE_HOST = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/iu;

/** The intake address the handle's helper shows as an example (card 22). */
function exampleAddress(handle: string): string {
  // eslint-disable-next-line i18next/no-literal-string -- an example intake address, the same in every language
  return `project.${handle}@intake.lexside.ai`;
}

function TopologyOption({ topology, checked, disabled, quota, full, onSelect, focusable }: {
  readonly topology: ZoneTopology; readonly checked: boolean; readonly disabled: boolean;
  readonly quota: string; readonly full: boolean; readonly onSelect: () => void; readonly focusable: boolean;
}) {
  const { t } = useTranslation('console');
  const icon = { laptop: <LaptopOutlined />, firm: <BankOutlined />, cloud: <CloudOutlined /> }[topology];
  const copy = {
    icon,
    title: t(`deploy.option.${topology}.title`),
    claim: t(`deploy.option.${topology}.claim`),
    fact: <Trans t={t} i18nKey={`deploy.option.${topology}.fact`} components={{ b: <b /> }} />,
  };
  // A radio group has one tab stop; arrow keys move between the cards (handled by the group).
  return <button type="button" role="radio" aria-checked={checked} className="cp-topology-option" disabled={disabled}
    data-topology={topology} onClick={onSelect} tabIndex={focusable ? 0 : -1}>
    <span className="cp-topology-head">{copy.icon}{copy.title}</span>
    <span className="cp-topology-claim">{copy.claim}</span>
    <span className="cp-topology-fact">{copy.fact}</span>
    <span className={full ? 'cp-topology-quota is-full' : 'cp-topology-quota'}>{quota}</span>
  </button>;
}

/** A cloud create under way in the modal (§3.2.2): its zone, polled, and the inputs Try again reuses. */
interface CloudStarted { readonly zone: Zone; readonly createdAt: number; readonly inputs: DeployForm }

const POWER_ACTION_REFRESH_MS = 60_000;
const sleepFor = (ms: number) => new Promise<void>(resolve => { window.setTimeout(resolve, ms); });

/** CP-3 (spec §3.10, reference design P2–P4): where the zone runs, then its details. */
function DeployZoneModal({ open, account, zones, onClose, onCreated, onZoneChanged, onZoneRemoved, onUnauthorized }: {
  readonly open: boolean; readonly account: Account; readonly zones: readonly Zone[];
  readonly onClose: () => void; readonly onCreated: (zone: CreatedZone) => void;
  readonly onZoneChanged: (zone: Zone) => void; readonly onZoneRemoved: (id: string) => void; readonly onUnauthorized: () => void;
}) {
  const { t } = useTranslation('console');
  const [form] = Form.useForm<DeployForm>();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [quotaRefused, setQuotaRefused] = useState<ReadonlySet<DeployTopology>>(new Set());
  const [offer, setOffer] = useState<CloudOffer | null>(null);
  const [started, setStarted] = useState<CloudStarted | null>(null);
  const [recreating, setRecreating] = useState(false);
  // The operation's generation: bumped when the modal opens or closes, unmounts, or its started zone is
  // deleted or replaced, so a recreation loop or a poll in flight from an older one does nothing more.
  const generation = useRef(0);
  const bump = () => { generation.current += 1; return generation.current; };
  useEffect(() => () => { generation.current += 1; }, []);
  const used = {
    laptop: zones.filter(zone => topologyOf(zone) === 'laptop').length,
    firm: zones.filter(zone => topologyOf(zone) === 'firm').length,
    cloud: zones.filter(zone => topologyOf(zone) === 'cloud').length,
  };
  const cloudOffered = offer?.enabled === true;
  const full = (topology: DeployTopology) => quotaRefused.has(topology) || used[topology] >= ZONE_QUOTAS[topology];
  const available = (topology: DeployTopology) => !full(topology)
    && (topology === 'laptop' || (topology === 'firm' && FIRM_DEPLOYMENT_ENABLED) || (topology === 'cloud' && cloudOffered));
  const [topology, setTopology] = useState<DeployTopology>('laptop');
  const group = useRef<HTMLDivElement>(null);
  // The handle (readable intake addresses §3.1.3): checked as it is typed, each answer kept for this opening;
  // false once the Worker answers the check 404 (it predates handles), so the create sends none.
  const checks = useRef(new Map<string, Exclude<HandleCheck, 'unsupported'>>());
  const [handles, setHandles] = useState<boolean | null>(null);
  // The handle follows the zone name until the deployer edits it (card 22), and again once they clear it.
  const followed = useRef('');
  const follow = (name: string) => {
    const current = form.getFieldValue('handle') as string | undefined ?? '';
    if (current !== '' && current !== followed.current) return;
    followed.current = suggestHandle(name) ?? '';
    form.setFieldValue('handle', followed.current);
  };
  const handleField: 'tenantName' | 'handle' = topology === 'cloud' ? 'tenantName' : 'handle';
  // Two watches: one watch does not follow a change of name.
  const laptopHandle = Form.useWatch('handle', form) as string | undefined;
  const cloudHandle = Form.useWatch('tenantName', form) as string | undefined;
  const typedHandle = topology === 'cloud' ? cloudHandle : laptopHandle;
  useEffect(() => {
    bump();
    if (!open) return;
    setCreateError(null); setQuotaRefused(new Set()); setStarted(null); setRecreating(false); setCreating(false); form.resetFields();
    checks.current = new Map(); setHandles(null); followed.current = '';
    // From the account's zones alone: an earlier session's refusals are being cleared.
    const laptopOpen = used.laptop < ZONE_QUOTAS.laptop;
    const firmOpen = FIRM_DEPLOYMENT_ENABLED && used.firm < ZONE_QUOTAS.firm;
    const start: DeployTopology = !laptopOpen && firmOpen ? 'firm' : 'laptop';
    setTopology(start);
    form.setFieldValue('name', defaultName(t, start));
    follow(defaultName(t, start));
    // The cloud card is live only when the control plane offers it (§3.2.2).
    let current = true;
    controlApi.cloudOffer().then((answer) => { if (current) setOffer(answer); }, (caught: unknown) => {
      if (!current) return;
      if (caught instanceof ApiError && caught.status === 401) onUnauthorized();
      else setOffer({ enabled: false });
    });
    return () => { current = false; };
    // Opening the modal decides the starting card; later zone changes do not move it.
  }, [open]);
  useEffect(() => {
    if (topology !== 'cloud' || offer?.enabled !== true || form.getFieldValue('region') !== undefined) return;
    // eslint-disable-next-line i18next/no-literal-string -- an IANA time zone id
    const timeZone = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })();
    form.setFieldValue('region', defaultRegion(offer.regions, timeZone, offer.suggestedRegion));
  }, [topology, offer, form]);
  // Step 3 polls its zone every 3 s while open (§3.2.2).
  const startedId = started?.zone.id;
  const startedStage = started === null ? null : cloudStage(started.zone);
  useEffect(() => {
    // A zone the China check left asleep is polled too, so its wake again shows (part 2 §4 I3).
    if (!open || startedId === undefined || (startedStage !== 'starting' && startedStage !== 'stopped') || recreating) return;
    const timer = window.setInterval(() => {
      const mine = generation.current;
      controlApi.zone(startedId).then(({ zone }) => {
        // A read from before a close, a deletion or a replacement never touches the modal or the list.
        if (mine !== generation.current) return;
        setStarted(current => current?.zone.id === zone.id ? { ...current, zone } : current);
        onZoneChanged(zone);
      }, (caught: unknown) => { if (mine === generation.current && caught instanceof ApiError && caught.status === 401) onUnauthorized(); });
    }, 3_000);
    return () => window.clearInterval(timer);
  }, [open, startedId, startedStage, recreating, onZoneChanged, onUnauthorized]);
  /** The check's answer for one handle, kept; null when it could not be read. */
  const checkHandle = async (handle: string, signal?: AbortSignal): Promise<HandleCheck | null> => {
    const known = checks.current.get(handle);
    if (known !== undefined) return known;
    try {
      const { available } = await controlApi.handleAvailable(handle, signal);
      checks.current.set(handle, available ? 'available' : 'taken');
    } catch (caught) {
      if (!(caught instanceof ApiError)) return null;
      if (caught.status === 401) { onUnauthorized(); return null; }
      if (caught.status === 404) { setHandles(false); return 'unsupported'; }
      if (caught.code !== 'invalid_handle') return null;
      checks.current.set(handle, 'invalid');
    }
    setHandles(true);
    return checks.current.get(handle) ?? null;
  };
  useEffect(() => {
    if (!open || handles === false || typedHandle === undefined || !HANDLE.test(typedHandle) || checks.current.has(typedHandle)) return;
    const controller = new AbortController();
    const field = handleField;
    const timer = window.setTimeout(() => {
      void checkHandle(typedHandle, controller.signal).then((check) => {
        if (check !== null && check !== 'unsupported' && !controller.signal.aborted) form.validateFields([field]).catch(() => {});
      });
    }, HANDLE_CHECK_MS);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [open, handles, typedHandle, handleField]);
  const takenMessage = (kind: DeployTopology, handle: string | undefined) => kind === 'cloud'
    ? t('deploy.addressTaken', { address: `${handle ?? ''}.${offer?.enabled === true ? offer.tenantDomain : ''}` }) : t('deploy.handleTaken');
  /** As typed: the rule, then what the check answered for this value. */
  const handleError = (kind: DeployTopology, value: unknown): string | null => {
    if (typeof value !== 'string' || value === '') return null;
    const check = checks.current.get(value);
    if (!HANDLE.test(value) || check === 'invalid') return t('deploy.invalidHandle');
    return check === 'taken' ? takenMessage(kind, value) : null;
  };
  const choose = (next: DeployTopology) => {
    const name = form.getFieldValue('name') as string | undefined;
    if (name === undefined || name === '' || name === defaultName(t, topology)) { form.setFieldValue('name', defaultName(t, next)); follow(defaultName(t, next)); }
    setTopology(next);
  };
  const order: DeployTopology[] = ['laptop', 'firm', 'cloud'];
  // The group's one tab stop: the selected card while it is available, else the first available.
  const tabStop = available(topology) ? topology : order.find(available);
  const onGroupKey = (event: React.KeyboardEvent) => {
    // eslint-disable-next-line i18next/no-literal-string -- key names
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const enabled = order.filter(available);
    if (enabled.length === 0) return;
    const at = enabled.indexOf(topology);
    const next = enabled[((at === -1 ? 0 : at + step) + enabled.length) % enabled.length]!;
    choose(next);
    group.current?.querySelector<HTMLButtonElement>(`[data-topology="${next}"]`)?.focus();
  };
  const quotaLine = (kind: DeployTopology) => {
    const counts = { used: Math.min(used[kind], ZONE_QUOTAS[kind]), count: ZONE_QUOTAS[kind] };
    return full(kind) ? t(`deploy.quota.${kind}Full`, counts) : t(`deploy.quota.${kind}`, counts);
  };
  /** The create, by topology; a cloud zone's web address is its handle, sent under both keys (§3.1.3). */
  const createRequest = (kind: DeployTopology, values: DeployForm, withHandle: boolean): CreateZoneRequest => {
    const handle = kind === 'cloud' ? values.tenantName ?? '' : values.handle ?? '';
    const named = withHandle ? { handle } : {};
    return kind === 'firm'
      ? { name: values.name, topology: 'firm', ownerEmail: values.ownerEmail?.trim() ?? '', zoneHost: values.zoneHost?.trim() ?? '', ...named }
      : kind === 'cloud'
        ? { name: values.name, topology: 'cloud', ownerEmail: values.ownerEmail?.trim() ?? '', tenantName: values.tenantName ?? '', ...named, region: values.region ?? '' }
        : { name: values.name, ...named };
  };
  const refused = (kind: DeployTopology, values: DeployForm, caught: unknown) => {
    const tenantDomain = offer?.enabled === true ? offer.tenantDomain : '';
    if (caught instanceof ApiError && caught.status === 401) onUnauthorized();
    else if (caught instanceof ApiError && caught.code === 'invalid_owner_email') {
      form.setFields([{ name: 'ownerEmail', errors: [t('deploy.ownerEmail.invalid', { example: 'name@firm.com' })] }]);
    } else if (caught instanceof ApiError && caught.code === 'invalid_zone_host') {
      form.setFields([{ name: 'zoneHost', errors: [t('deploy.zoneHost.invalid', { example: ZONE_HOST_EXAMPLE })] }]);
    } else if (caught instanceof ApiError && caught.code === 'tenant_name_taken') {
      form.setFields([{ name: 'tenantName', errors: [t('deploy.addressTaken', { address: `${values.tenantName}.${tenantDomain}` })] }]);
    } else if (caught instanceof ApiError && caught.code === 'invalid_tenant_name') {
      form.setFields([{ name: 'tenantName', errors: [t('deploy.invalidHandle')] }]);
    } else if (caught instanceof ApiError && (caught.code === 'handle_taken' || caught.code === 'invalid_handle' || caught.code === 'relay_name_unavailable')) {
      const field: 'tenantName' | 'handle' = kind === 'cloud' ? 'tenantName' : 'handle';
      form.setFields([{ name: field, errors: [caught.code === 'handle_taken' ? takenMessage(kind, values[field])
        : caught.code === 'invalid_handle' ? t('deploy.invalidHandle') : t('deploy.relayNameUnavailable')] }]);
    } else if (caught instanceof ApiError && caught.code === 'invalid_region') {
      form.setFields([{ name: 'region', errors: [t('deploy.regionUnavailable')] }]);
      controlApi.cloudOffer().then(setOffer, () => {});
    } else if (caught instanceof ApiError && caught.code === 'quota_reached') {
      setQuotaRefused(current => new Set([...current, kind]));
    } else if (caught instanceof ApiError && caught.code === 'topology_unavailable') {
      setCreateError(t('deploy.topologyUnavailable'));
    } else setCreateError(errorMessage(t, caught));
  };
  const create = async (values: DeployForm) => {
    setCreating(true); setCreateError(null);
    // Fenced like Try again: an answer that lands after the modal closed or reopened only reaches the list.
    const mine = generation.current;
    const kind = topology;
    let created: CreatedZone;
    try {
      // Never a handle the Worker would refuse: a check not yet answered is asked now (§3.1.3).
      const withHandle = handles === true
        || (handles === null && await checkHandle(kind === 'cloud' ? values.tenantName ?? '' : values.handle ?? '') !== 'unsupported');
      if (mine !== generation.current) return;
      // The Handle field has just gone: let the deployer read why before creating without one.
      if (!withHandle && handles === null && kind !== 'cloud') { setCreating(false); return; }
      created = await controlApi.createZone(createRequest(kind, values, withHandle));
    } catch (caught) {
      if (mine === generation.current) { refused(kind, values, caught); setCreating(false); }
      return;
    }
    if (mine !== generation.current) { onZoneChanged(created.zone); return; }
    try {
      if (kind === 'cloud') {
        // Nothing to install: step 3 narrates the start (§3.2.2).
        bump();
        onZoneChanged(created.zone);
        setStarted({ zone: created.zone, createdAt: Date.now(), inputs: values });
      } else onCreated(created);
    } finally { setCreating(false); }
  };
  /**
   * Delete the failed zone; the operation's new generation once the deletion is recorded (204) or the zone
   * is already gone, or null (the error shown, or the modal moved on meanwhile).
   */
  const removeFailed = async (zone: Zone, mine: number): Promise<number | null> => {
    try { await controlApi.deleteZone(zone.id); }
    catch (caught) {
      if (mine !== generation.current) return null;
      if (caught instanceof ApiError && caught.status === 401) { onUnauthorized(); return null; }
      if (!(caught instanceof ApiError && caught.status === 404)) { setCreateError(errorMessage(t, caught)); return null; }
    }
    if (mine !== generation.current) return null;
    // Outstanding polls of the deleted zone are now stale.
    const next = bump();
    onZoneRemoved(zone.id);
    return next;
  };
  /**
   * Try again (§3.2.2): delete the failed zone, then create it again with the same inputs, retrying every 10 s
   * for up to 5 minutes while the old address or quota is not yet released; after that the form shows why.
   */
  const tryAgain = async () => {
    if (started === null) return;
    const { zone: failed, inputs } = started;
    setRecreating(true); setCreateError(null);
    try {
      const mine = await removeFailed(failed, generation.current);
      if (mine === null) return;
      const began = Date.now();
      for (;;) {
        try {
          const created = await controlApi.createZone(createRequest('cloud', inputs, handles !== false));
          // Closed meanwhile: the zone exists and the list shows it, but this modal no longer narrates it.
          if (mine !== generation.current) { onZoneChanged(created.zone); return; }
          bump();
          onZoneChanged(created.zone);
          setStarted({ zone: created.zone, createdAt: Date.now(), inputs });
          return;
        } catch (caught) {
          if (mine !== generation.current) return;
          const code = caught instanceof ApiError ? caught.code : 'request_failed';
          if (recreateAction(code, Date.now() - began) === 'retry') {
            await sleepFor(RECREATE_RETRY_MS);
            if (mine !== generation.current) return;
            continue;
          }
          // Back to the form, its inputs kept, with the reason on its field.
          setStarted(null);
          form.setFieldsValue(inputs);
          refused('cloud', inputs, caught);
          if (code === 'tenant_name_taken' || code === 'handle_taken') {
            form.setFields([{ name: 'tenantName', errors: [t('deploy.addressReleasing')] }]);
          }
          return;
        }
      }
    } finally { setRecreating(false); }
  };
  const chooseDifferent = async () => {
    if (started === null) return;
    setRecreating(true);
    try {
      if (await removeFailed(started.zone, generation.current) !== null) { setStarted(null); form.setFieldsValue(started.inputs); }
    } finally { setRecreating(false); }
  };
  const firmQuota = !FIRM_DEPLOYMENT_ENABLED ? t('deploy.quota.firmWithAddins') : quotaLine('firm');
  // eslint-disable-next-line i18next/no-literal-string -- a dash while the offer is read
  const cloudQuota = offer === null ? '—' : !cloudOffered ? t('deploy.quota.cloudUnavailable') : quotaLine('cloud');
  return <Modal title={t('deploy.title')} open={open} onCancel={onClose} footer={null} width={760} style={{ top: 40 }} destroyOnClose>
    {started !== null && createError !== null && <Alert className="cp-create-error" type="error" showIcon message={createError} />}
    {started !== null ? <CloudStart zone={started.zone} createdAt={started.createdAt} recreating={recreating}
      onTryAgain={() => void tryAgain()} onChooseDifferent={() => void chooseDifferent()} /> : <>
    <Paragraph type="secondary">{t('deploy.intro')}</Paragraph>
    <div className="cp-topology" role="radiogroup" aria-label={t('deploy.where')} ref={group} onKeyDown={onGroupKey}>
      <TopologyOption topology="laptop" checked={topology === 'laptop'} disabled={!available('laptop')} quota={quotaLine('laptop')}
        full={full('laptop')} onSelect={() => choose('laptop')} focusable={tabStop === 'laptop'} />
      <TopologyOption topology="firm" checked={topology === 'firm'} disabled={!available('firm')} quota={firmQuota}
        full={FIRM_DEPLOYMENT_ENABLED && full('firm')} onSelect={() => choose('firm')} focusable={tabStop === 'firm'} />
      <TopologyOption topology="cloud" checked={topology === 'cloud'} disabled={!available('cloud')} quota={cloudQuota}
        full={cloudOffered && full('cloud')} onSelect={() => choose('cloud')} focusable={tabStop === 'cloud'} />
    </div>
    {createError !== null && <Alert className="cp-create-error" type="error" showIcon message={createError} />}
    <Form form={form} layout="vertical" requiredMark={false} onFinish={create} initialValues={{ ownerEmail: account.email }}
      onValuesChange={(changed: Partial<DeployForm>) => { if (changed.name !== undefined) follow(changed.name); }}>
      <Form.Item name="name" label={t('deploy.name.label')} rules={[{ required: true, whitespace: true, max: 100, message: t('deploy.name.required') }]}>
        <Input autoFocus maxLength={100} placeholder={topology === 'laptop' ? t('deploy.name.placeholder') : ''} onKeyDown={holdComposingEnter} />
      </Form.Item>
      {topology !== 'cloud' && (handles === false
        ? <Paragraph type="secondary" className="cp-handle-later">{t('deploy.handleLater')}</Paragraph>
        : <Form.Item name="handle" label={t('zone.handle.label')} normalize={(value: string) => value.toLowerCase()}
          extra={<Trans t={t} i18nKey="deploy.handleHelp" values={{ address: exampleAddress(exampleHandle(typedHandle)) }} components={{ code: <code /> }} />}
          rules={[{ required: true, message: t('deploy.handleRequired') },
            { validator: async (_rule, value: unknown) => { const error = handleError(topology, value); if (error !== null) throw new Error(error); } }]}>
          <Input className="cp-handle-input" placeholder={topology === 'laptop' ? 'my-macbook' : ''} maxLength={HANDLE_MAX} autoComplete="off" spellCheck={false} onKeyDown={holdComposingEnter} />
        </Form.Item>)}
      {topology === 'firm' && <>
        <Form.Item name="ownerEmail" label={t('deploy.ownerEmail.label')}
          extra={t('deploy.ownerEmail.help')}
          rules={[{ required: true, whitespace: true, type: 'email', message: t('deploy.ownerEmail.invalid', { example: 'name@firm.com' }) }]}>
          <Input placeholder="name@firm.com" maxLength={254} onKeyDown={holdComposingEnter} />
        </Form.Item>
        <Form.Item name="zoneHost" label={t('deploy.zoneHost.label')}
          extra={<Trans t={t} i18nKey="deploy.zoneHost.help" values={{ address: 'https://<name>:47800' }} components={{ code: <code /> }} />}
          rules={[{ required: true, message: t('deploy.zoneHost.invalid', { example: ZONE_HOST_EXAMPLE }) },
            { validator: async (_rule, value: unknown) => {
              if (typeof value === 'string' && value.trim() !== '' && !ZONE_HOST.test(value.trim())) throw new Error(t('deploy.zoneHost.invalid', { example: ZONE_HOST_EXAMPLE }));
            } }]}>
          <Input placeholder={ZONE_HOST_EXAMPLE} maxLength={253} onKeyDown={holdComposingEnter} />
        </Form.Item>
      </>}
      {topology === 'cloud' && offer?.enabled === true && <CloudDetailsFields offer={offer} handles={handles !== false}
        example={exampleHandle(typedHandle)} handleError={(value) => handleError('cloud', value)} />}
      <div className="cp-modal-footer"><Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button type="primary" htmlType="submit" loading={creating} disabled={!available(topology)}>{t('deploy.create')}</Button></div>
    </Form></>}
  </Modal>;
}

function ZonesPage({ account, navigate, onUnauthorized, zones, loading, error, loadZones, upsertZone, removeZone }:
  Pick<CommonProps, 'account' | 'navigate' | 'onUnauthorized'> & { readonly zones: readonly Zone[];
    readonly loading: boolean; readonly error: string | null; readonly loadZones: () => Promise<void>;
    readonly upsertZone: (zone: Zone) => void; readonly removeZone: (id: string) => void }) {
  const { t } = useTranslation('console');
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<CreatedZone | null>(null);
  const [deleteZone, setDeleteZone] = useState<Zone | null>(null);

  const closeCreate = () => { setCreateOpen(false); setCreated(null); };

  return <div className="cp-page">
    <div className="cp-page-heading"><div><Title level={1}>{t('shell.zones')}</Title>
      <Paragraph type="secondary">{t('zones.intro')}</Paragraph></div>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>{t('zones.deployNew')}</Button>
    </div>
    {error !== null && <Alert type="error" showIcon message={error} action={<Button onClick={() => void loadZones()}>{t('common.tryAgain')}</Button>} />}
    {loading ? <div className="cp-zone-grid">{[0, 1, 2].map((key) => <Card key={key}><Skeleton active paragraph={{ rows: 4 }} /></Card>)}</div>
      : zones.length === 0 ? <Card className="cp-empty"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('zones.empty')}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>{t('zones.deployFirst')}</Button>
      </Empty></Card>
        : <div className="cp-zone-grid">{zones.map((zone) => <ZoneCard key={zone.id} zone={zone} navigate={navigate} onDelete={setDeleteZone} />)}
          <button className="cp-create-tile" onClick={() => setCreateOpen(true)}><PlusOutlined /><span>{t('zones.deployAnother')}</span></button>
        </div>}
    <DeployZoneModal open={createOpen && created === null} account={account} zones={zones} onClose={closeCreate} onUnauthorized={onUnauthorized}
      onCreated={(result) => { setCreated(result); upsertZone(result.zone); }}
      onZoneChanged={upsertZone} onZoneRemoved={removeZone} />
    <InstallCommandModal open={createOpen && created !== null} zone={created?.zone ?? null}
      command={created?.claimToken === undefined ? null : created as InstallCommand} onClose={closeCreate} />
    <DeleteZoneModal zone={deleteZone} open={deleteZone !== null} onClose={() => setDeleteZone(null)}
      onDeleted={(zone) => removeZone(zone.id)} onUnauthorized={onUnauthorized} />
  </div>;
}

function ZoneDetailPage({ id, navigate, onUnauthorized, onZoneDeleted, refresh, onPowerAction }:
  Pick<CommonProps, 'navigate' | 'onUnauthorized'> & { readonly id: string;
    readonly onZoneDeleted: (zone: Zone) => void; readonly refresh: number; readonly onPowerAction: () => void }) {
  const { t } = useTranslation('console');
  const format = useFormat();
  const [zone, setZone] = useState<Zone | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [command, setCommand] = useState<InstallCommand | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const [forceConfirmation, setForceConfirmation] = useState('');
  const [forcing, setForcing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) { setLoading(true); setError(null); setNotFound(false); }
    try { const result = await controlApi.zone(id); setZone(result.zone); }
    catch (caught) {
      const action = refreshFailureAction(caught instanceof ApiError ? caught.status : undefined, silent);
      if (action === 'unauthorized') onUnauthorized();
      else if (action === 'error') {
        if (caught instanceof ApiError && caught.status === 404) setNotFound(true);
        else setError(errorMessage(consoleT(), caught));
      }
    } finally { if (!silent) setLoading(false); }
  }, [id, onUnauthorized]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (refresh > 0) void load(true); }, [refresh, load]);

  const regenerate = async () => {
    if (zone === null) return;
    setRegenerating(true); setError(null);
    try {
      const result = await controlApi.regenerateClaimToken(zone.id); setCommand(result);
      setZone({ ...zone, claimExpiresAt: result.claimExpiresAt ?? zone.claimExpiresAt });
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onUnauthorized();
      else setError(errorMessage(t, caught));
    } finally { setRegenerating(false); }
  };

  const rotate = async () => {
    setRotating(true); setError(null);
    try { await controlApi.rotateToken(id); await load(); }
    catch (caught) { setError(errorMessage(t, caught)); }
    finally { setRotating(false); }
  };

  const forceRevoke = async () => {
    setForcing(true); setError(null);
    try {
      await controlApi.forceRevokeToken(id); setForceOpen(false); setForceConfirmation(''); await load();
    } catch (caught) { setError(errorMessage(t, caught)); }
    finally { setForcing(false); }
  };

  if (loading) return <div className="cp-page"><Skeleton active paragraph={{ rows: 8 }} /></div>;
  if (notFound) return <Result status="404" title={t('detail.notFound')} subTitle={t('detail.notFoundDetail')}
    extra={<Button type="primary" onClick={() => navigate('/zones')}>{t('detail.back')}</Button>} />;
  if (zone === null) return <div className="cp-page"><Alert type="error" showIcon message={error ?? t('detail.loadFailed')}
    action={<Button onClick={() => void load()}>{t('common.tryAgain')}</Button>} /></div>;

  const cloud = cloudOf(zone);
  if (cloud !== null) return <CloudZoneDetail zone={zone} navigate={navigate} onUnauthorized={onUnauthorized} reload={() => void load(true)}
    error={error} onZoneDeleted={onZoneDeleted} onPowerAction={onPowerAction} />;
  const expired = zone.status === 'awaiting_setup' && zone.claimExpiresAt !== null && zone.claimExpiresAt <= Date.now();
  const firm = topologyOf(zone) === 'firm';
  const address = zoneAddress(zone);
  return <div className="cp-page cp-detail">
    <Breadcrumb items={[{ title: <button onClick={() => navigate('/zones')}>{t('shell.zones')}</button> }, { title: zone.name }]} />
    <div className="cp-detail-heading"><div className="cp-zone-glyph"><TopologyIcon zone={zone} /></div><div>
      <Title level={1}>{zone.name}</Title><Space wrap><TopologyTag zone={zone} /><ZoneStatusTag zone={zone} /></Space>
    </div></div>
    {zone.status === 'awaiting_setup' && <Alert type={expired ? 'warning' : 'info'} showIcon
      message={expired ? t('detail.setup.expiredTitle') : t('detail.setup.awaitingTitle')}
      description={expired ? firm ? t('detail.setup.expiredServer') : t('detail.setup.expiredMac') : t('detail.setup.awaiting')}
      action={<Popconfirm title={t('detail.setup.replaceTitle')} description={t('detail.setup.replaceDetail')}
        onConfirm={() => void regenerate()} okText={t('detail.setup.replaceOk')}><Button type="primary" icon={<ReloadOutlined />} loading={regenerating}>{t('detail.setup.regenerate')}</Button></Popconfirm>} />}
    {zone.status === 'claimed' && <Alert type="info" showIcon message={t('detail.claimedTitle')}
      description={t('detail.claimed')} />}
    {isOffline(zone) && <Alert type="warning" showIcon message={t('detail.offlineTitle')}
      description={firm ? t('detail.offlineFirm') : t('detail.offlineLaptop')} />}
    {zone.pendingRotationSince !== null && <Alert type="warning" showIcon message={t('detail.rotationPendingTitle')}
      description={t('detail.rotationPending', { when: dateTime(t, format, zone.pendingRotationSince) })} />}
    {error !== null && <Alert type="error" showIcon message={error} />}
    <UpgradeCommandCard zone={zone} />
    <Card title={t('detail.facts')} className="cp-facts">{firm
      ? <Descriptions column={{ xs: 1, sm: 2, lg: 3 }}>
        <Descriptions.Item label={t('detail.fact.status')}><ZoneStatusTag zone={zone} /></Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.topology')}>{t('zone.topology.firm')}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.created')}>{dateTime(t, format, zone.createdAt)}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.address')} span={2}>{address === null ? t('common.notSet') : <Typography.Link href={address} target="_blank" rel="noreferrer">{address}</Typography.Link>}</Descriptions.Item>
        {handleFact(t, zone)}
        <Descriptions.Item label={t('detail.fact.ownerEmail')}>{zone.ownerEmail ?? t('common.notSet')}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.users')}>{typeof zone.userCount === 'number' ? t('detail.fact.usersAsOf', { count: zone.userCount }) : t('common.notYet')}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.lastCheckin')}>{dateTime(t, format, zone.lastCheckinAt)}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.configVersion')} span={zone.handle === undefined ? 1 : 'filled'}>{zone.configVersion}</Descriptions.Item>
        {zone.status === 'awaiting_setup' && <Descriptions.Item label={t('detail.fact.claimExpires')} span={3}>{dateTime(t, format, zone.claimExpiresAt)}</Descriptions.Item>}
        <Descriptions.Item label={t('detail.fact.zoneId')} span={3}><code className="ant-typography">{zone.id}</code></Descriptions.Item>
      </Descriptions>
      : <Descriptions column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item label={t('detail.fact.status')}><ZoneStatusTag zone={zone} /></Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.topology')}>{t('zone.topology.laptop')}</Descriptions.Item>
        {handleFact(t, zone, 2)}
        <Descriptions.Item label={t('detail.fact.created')}>{dateTime(t, format, zone.createdAt)}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.claimed')}>{dateTime(t, format, zone.claimedAt)}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.lastCheckin')}>{dateTime(t, format, zone.lastCheckinAt)}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.configVersion')}>{zone.configVersion}</Descriptions.Item>
        {zone.status === 'awaiting_setup' && <Descriptions.Item label={t('detail.fact.claimExpires')}>{dateTime(t, format, zone.claimExpiresAt)}</Descriptions.Item>}
        <Descriptions.Item label={t('detail.fact.zoneId')} span={2}><Text code>{zone.id}</Text></Descriptions.Item>
      </Descriptions>}
      <div className="cp-privacy"><SafetyCertificateOutlined /><span>{t('detail.privacyFirm')}</span></div>
    </Card>
    {zone.status !== 'awaiting_setup' && <Card title={t('detail.credential.title')} className="cp-facts">
      <Space direction="vertical" size="middle">
        <Paragraph type="secondary">{t('detail.credential.intro')}</Paragraph>
        <Space wrap>
          <Popconfirm title={t('detail.credential.rotateTitle')} description={t('detail.credential.rotateDetail')}
            onConfirm={() => void rotate()} okText={t('detail.credential.rotateOk')}><Button loading={rotating} icon={<ReloadOutlined />}>{t('detail.credential.rotate')}</Button></Popconfirm>
          <Button danger onClick={() => setForceOpen(true)}>{t('detail.credential.forceRevoke')}</Button>
        </Space>
      </Space>
    </Card>}
    <Card title={t('detail.danger.title')} className="cp-danger"><div><Text strong>{t('detail.danger.heading')}</Text>
      <Paragraph type="secondary">{firm ? t('detail.danger.firm') : t('detail.danger.laptop')}</Paragraph></div>
      <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteOpen(true)}>{t('zone.deleteZone')}</Button>
    </Card>
    <InstallCommandModal open={command !== null} zone={zone} command={command} onClose={() => setCommand(null)} />
    <DeleteZoneModal zone={zone} open={deleteOpen} onClose={() => setDeleteOpen(false)} onDeleted={(deletedZone) => {
      onZoneDeleted(deletedZone); navigate('/zones');
    }} onUnauthorized={onUnauthorized} />
    <Modal title={t('detail.credential.forceTitle')} open={forceOpen} okText={t('detail.credential.forceOk')}
      okButtonProps={{ danger: true, loading: forcing, disabled: forceConfirmation !== zone.id }}
      onOk={() => void forceRevoke()} onCancel={() => { setForceOpen(false); setForceConfirmation(''); }}>
      <Alert type="error" showIcon message={t('detail.credential.forceWarning')}
        description={t('detail.credential.forceDetail')} />
      <Paragraph className="cp-confirm-copy"><Trans t={t} i18nKey="delete.typeToConfirm" values={{ name: zone.id }} components={{ code: <Text code /> }} /></Paragraph>
      <Input value={forceConfirmation} onChange={(event) => setForceConfirmation(event.target.value)} />
    </Modal>
  </div>;
}

/** CP-5 for a cloud zone (card 24): power, waking before work, its facts; no install or upgrade copy. */
function CloudZoneDetail({ zone, navigate, onUnauthorized, reload, error, onZoneDeleted, onPowerAction }: Pick<CommonProps, 'navigate' | 'onUnauthorized'> & {
  readonly zone: Zone; readonly reload: () => void; readonly error: string | null; readonly onZoneDeleted: (zone: Zone) => void;
  readonly onPowerAction: () => void;
}) {
  const { t } = useTranslation('console');
  const format = useFormat();
  const cloud = cloudOf(zone)!;
  const [deleteOpen, setDeleteOpen] = useState(false);
  const failed = cloud.lifecycle === 'failed';
  return <div className="cp-page cp-detail">
    <Breadcrumb items={[{ title: <button onClick={() => navigate('/zones')}>{t('shell.zones')}</button> }, { title: zone.name }]} />
    <div className="cp-detail-heading"><div className="cp-zone-glyph"><TopologyIcon zone={zone} /></div><div className="cp-detail-title">
      <Title level={1}>{zone.name}</Title><Space wrap><TopologyTag zone={zone} /><ZoneStatusTag zone={zone} /></Space>
    </div><div className="cp-detail-actions"><OpenZoneButton zone={zone} label={t('cloud.openTrustedZone')} /></div></div>
    {failed && <Alert type="error" showIcon message={cloud.error === 'capacity' ? t('cloudDetail.failedCapacity') : t('cloudDetail.failedOther')}
      description={t('cloudDetail.failedDetail')} />}
    {cloud.lifecycle === 'provisioning' && <Alert type="info" showIcon message={t('cloudDetail.startingTitle')}
      description={t('cloudDetail.starting')} />}
    {wakeDidNotFinish(cloud) && <Alert type="warning" showIcon message={t('cloudDetail.wakeNotReadyTitle')}
      description={t('cloudDetail.wakeNotReady')} />}
    {cloud.powerError === 'thaw_failed' && cloud.power === 'asleep' && <Alert type="warning" showIcon message={t('cloudDetail.thawFailedTitle')}
      description={t('cloudDetail.thawFailed')} />}
    {cloud.powerError === 'az_restore_aborted' && cloud.power === 'asleep' && <Alert type="warning" showIcon message={t('cloudDetail.azRestoreTitle')}
      description={t('cloudDetail.azRestore')} />}
    {error !== null && <Alert type="error" showIcon message={error} />}
    <PowerPanel zone={zone} onChanged={() => { reload(); onPowerAction(); }} onUnauthorized={onUnauthorized} />
    <WakeBeforeWork zone={zone} onSaved={reload} onUnauthorized={onUnauthorized} />
    <Card title={t('cloudDetail.information')} className="cp-facts">
      <Descriptions column={{ xs: 1, sm: 2, lg: 3 }}>
        <Descriptions.Item label={t('detail.fact.topology')}>{t('zone.topology.cloud')}</Descriptions.Item>
        <Descriptions.Item label={t('cloud.details.addressLabel')} span={2}><Typography.Link href={`https://${cloud.webName}`} target="_blank" rel="noreferrer">https://{cloud.webName}</Typography.Link></Descriptions.Item>
        {handleFact(t, zone)}
        <Descriptions.Item label={t('cloud.details.regionLabel')}>{regionLabel(t, cloud.region)}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.ownerEmail')}>{zone.ownerEmail ?? t('common.notSet')}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.users')}>{typeof zone.userCount === 'number' ? t('detail.fact.usersAsOf', { count: zone.userCount }) : t('common.notYet')}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.created')}>{dateTime(t, format, zone.createdAt)}</Descriptions.Item>
        <Descriptions.Item label={t('detail.fact.zoneId')} span={zone.handle === undefined ? 2 : 'filled'}><code className="ant-typography">{zone.id}</code></Descriptions.Item>
      </Descriptions>
      <div className="cp-privacy"><SafetyCertificateOutlined /><span>{t('detail.privacyCloud')}</span></div>
    </Card>
    <Card title={t('detail.danger.title')} className="cp-danger"><div><Text strong>{t('detail.danger.heading')}</Text>
      <Paragraph type="secondary">{t('detail.danger.cloud')}</Paragraph></div>
      <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteOpen(true)}>{t('zone.deleteZone')}</Button>
    </Card>
    <DeleteZoneModal zone={zone} open={deleteOpen} onClose={() => setDeleteOpen(false)} onDeleted={(deletedZone) => {
      onZoneDeleted(deletedZone); navigate('/zones');
    }} onUnauthorized={onUnauthorized} />
  </div>;
}

export function ControlPlane(props: CommonProps & { readonly path: string }) {
  const [zones, setZones] = useState<Zone[]>([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [zonesError, setZonesError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const firstLoad = useRef(true);
  const zoneId = useMemo(() => {
    const match = /^\/zones\/([^/]+)$/u.exec(props.path);
    if (match?.[1] === undefined) return null;
    try { return decodeURIComponent(match[1]); } catch { return null; }
  }, [props.path]);
  // Bumped by every local change (a create, a poll's update, a removal): a list read issued before it is
  // stale — it could erase a new zone or bring a removed one back — so it is dropped and read again once.
  const localChanges = useRef(0);
  const loadZones = useCallback(async (silent = false, again = true): Promise<void> => {
    if (!silent) { setZonesLoading(true); setZonesError(null); }
    const before = localChanges.current;
    try {
      const result = await controlApi.listZones();
      if (before === localChanges.current) setZones(result.zones);
      else if (again) await loadZones(true, false);
    }
    catch (caught) {
      const action = refreshFailureAction(caught instanceof ApiError ? caught.status : undefined, silent);
      if (action === 'unauthorized') props.onUnauthorized();
      else if (action === 'error') setZonesError(errorMessage(consoleT(), caught));
    } finally { if (!silent) setZonesLoading(false); }
  }, [props.onUnauthorized]);
  const refreshZones = useCallback(() => { void loadZones(true); setRefresh(value => value + 1); }, [loadZones]);
  useEffect(() => {
    void loadZones(!firstLoad.current); firstLoad.current = false;
  }, [loadZones, props.path]);
  useEffect(() => {
    const focus = () => { if (refreshOnReturn('focus', document.visibilityState)) refreshZones(); };
    const visible = () => { if (refreshOnReturn('visibilitychange', document.visibilityState)) refreshZones(); };
    window.addEventListener('focus', focus); document.addEventListener('visibilitychange', visible);
    return () => { window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', visible); };
  }, [refreshZones]);
  // A power action refreshes every 3 s for a minute: the control plane answers the old power at once, and
  // publishes the transition when its alarm runs (§3.2.3).
  const [boostUntil, setBoostUntil] = useState(0);
  const boostRefresh = useCallback(() => setBoostUntil(Date.now() + POWER_ACTION_REFRESH_MS), []);
  useEffect(() => {
    if (boostUntil === 0) return;
    const timer = window.setTimeout(() => setBoostUntil(0), Math.max(0, boostUntil - Date.now()));
    return () => window.clearTimeout(timer);
  }, [boostUntil]);
  // Laptop and firm zones awaiting setup refresh every 15 s; cloud zones by their power (§3.2.3).
  const cloudMs = boostUntil !== 0 ? CLOUD_TRANSITION_REFRESH_MS : cloudRefreshMs(zones);
  const refreshMs = zonesNeedRefresh(zones.filter((zone) => topologyOf(zone) !== 'cloud'))
    ? Math.min(ZONE_REFRESH_MS, cloudMs ?? ZONE_REFRESH_MS) : cloudMs;
  useEffect(() => {
    if (refreshMs === null) return;
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') refreshZones(); }, refreshMs);
    return () => window.clearInterval(timer);
  }, [refreshMs, refreshZones]);

  const removeZone = useCallback((zone: Zone) => {
    localChanges.current += 1;
    setZones((current) => current.filter((item) => item.id !== zone.id));
  }, []);
  const removeZoneById = useCallback((id: string) => {
    localChanges.current += 1;
    setZones((current) => current.filter((item) => item.id !== id));
  }, []);
  // A create is shown in the list at once, and a cloud one kept current by the modal's polling (§3.2.2).
  const upsertZone = useCallback((zone: Zone) => {
    localChanges.current += 1;
    setZones((current) => current.some((item) => item.id === zone.id)
      ? current.map((item) => item.id === zone.id ? zone : item) : [zone, ...current]);
  }, []);

  return <ControlShell {...props} zones={zones} zonesLoading={zonesLoading} activeZoneId={zoneId}>
    {props.path === '/control/recovery'
      ? <RecoveryPage account={props.account} onUnauthorized={props.onUnauthorized} />
      : props.path === '/control/support/reports' || /^\/control\/support\/reports\/[a-f0-9-]{36}$/u.test(props.path)
      ? <SupportReports id={props.path.split('/')[4] ?? null} navigate={props.navigate} onUnauthorized={props.onUnauthorized}/>
      : zoneId === null
      ? <ZonesPage account={props.account} navigate={props.navigate} onUnauthorized={props.onUnauthorized} zones={zones}
        loading={zonesLoading} error={zonesError} loadZones={() => loadZones()} upsertZone={upsertZone} removeZone={removeZoneById} />
      : <ZoneDetailPage key={zoneId} refresh={refresh} id={zoneId} navigate={props.navigate} onUnauthorized={props.onUnauthorized}
        onZoneDeleted={removeZone} onPowerAction={boostRefresh} />}
  </ControlShell>;
}
